import React, { useEffect, useState, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import ProductGrid from "../components/ProductGrid";
import { db } from "../utils/firebaseConfig";
import { collection, getDocs } from "firebase/firestore";
import { toast } from "react-hot-toast";
import CategoriasGrid from "../components/CategoriasGrid";
import Breadcrumb from "../components/Breadcrumb";

export default function Catalogo() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const paginaActual = parseInt(searchParams.get("page") || "1");
  const productosRef = useRef(null);

  const getCategoriaBonita = (categoria) => {
    const key = normalize(categoria);
    if (key === "kimberly clark") return "Paños de limpieza";
    return categoria;
  };
  // === Helpers de búsqueda ===
  const normalize = (str = "") =>
    str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

  // Pequeño fuzzy: distancia de Levenshtein para tolerar 1–2 errores
  const lev = (a, b) => {
    a = normalize(a); b = normalize(b);
    const m = a.length, n = b.length;
    if (m === 0) return n; if (n === 0) return m;
    const dp = Array.from({ length: m + 1 }, (_, i) =>
      Array.from({ length: n + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
    );
    for (let i = 1; i <= m; i++) {
      for (let j = 1; j <= n; j++) {
        const cost = a[i - 1] === b[j - 1] ? 0 : 1;
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + cost
        );
      }
    }
    return dp[m][n];
  };

  // ¿Dos textos “parecidos”? incluye + tolerancia a 1–2 errores
  const similar = (haystack, needle) => {
    const H = normalize(haystack);
    const N = normalize(needle);
    if (!N) return true;
    if (H.includes(N)) return true;
    // Revisa por palabras: si alguna palabra es muy cercana, también cuenta
    const tokens = H.split(/[^a-z0-9]+/).filter(Boolean);
    for (const t of tokens) {
      if (t === N) return true;
      const d = lev(t, N);
      if ((N.length >= 5 && d <= 2) || (N.length >= 3 && d <= 1)) return true;
    }
    return false;
  };

  useEffect(() => {
    if (searchParams.get("scroll") === "1") {
      window.scrollTo({ top: 0, behavior: "auto" });
  
      const scrollWhenReady = () => {
        if (productosRef.current) {
          const yOffset = -300; // ajusta según la altura de tu header en móvil
          const y = productosRef.current.getBoundingClientRect().top + window.scrollY + yOffset;
          window.scrollTo({ top: y, behavior: "smooth" });
  
          // limpia el parámetro para que no siga scrolleando en navegación interna
          const updated = new URLSearchParams(searchParams);
          updated.delete("scroll");
          setSearchParams(updated, { replace: true });
          return;
        }
        // si aún no hay ref (render no listo), intenta en el próximo frame
        requestAnimationFrame(scrollWhenReady);
      };
  
      requestAnimationFrame(scrollWhenReady);
    }
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const cargarProductos = async () => {
      const local = localStorage.getItem("productos");
      if (local) {
        setProducts(JSON.parse(local));
        setLoading(false);
        return;
      }
      try {
        const snap = await getDocs(collection(db, "productos"));
        const datos = snap.docs.map((d) => {
          const data = d.data();
          return {
            id: d.id,
            ...data,
            nombre: data.nombre?.trim() || "",
            categoria: data.categoria?.toLowerCase().trim() || "",
            imagen: data.imagen?.includes("?v=") ? data.imagen : `${data.imagen}?v=20250731`,
          };
        });
        setProducts(datos);
        localStorage.setItem("productos", JSON.stringify(datos));
      } catch (error) {
        console.error("❌ Firebase error:", error);
        toast.error("No se pudo cargar el catálogo.");
      } finally {
        setLoading(false);
      }
    };
    cargarProductos();
  }, []);

  useEffect(() => {
    // Evita correr mientras aún carga
    if (loading) return;
  
    const doScroll = () => {
      if (!productosRef.current) {
        requestAnimationFrame(doScroll);
        return;
      }
      const yOffset = -300; // ajusta según la altura de tu header en móvil
      const y = productosRef.current.getBoundingClientRect().top + window.scrollY + yOffset;
  
      // 1) “rompe” la posición pegada al fondo para que el smooth funcione siempre
      window.scrollTo({ top: y, behavior: "auto" });
  
      // 2) y luego haz el smooth (siguiente frame) para una animación agradable
      requestAnimationFrame(() => {
        window.scrollTo({ top: y, behavior: "smooth" });
      });
    };
  
    // Espera 1 frame para que ProductGrid pinte la nueva página
    requestAnimationFrame(doScroll);
  }, [paginaActual, loading]);

  const filtroNombreRaw = (searchParams.get("nombre") || "");
  const filtroCategoriaRaw = (searchParams.get("categoria") || "");
  const filtroNombre = normalize(filtroNombreRaw);
  const filtroCategoria = normalize(filtroCategoriaRaw);
  
  // 1) Nombre primero (no trae toda cafetería si buscas "cafe")
  const resultadosNombre = useMemo(() => {
    return products.filter((p) => {
      const catOK = filtroCategoria ? normalize(p.categoria || "") === filtroCategoria : true;
      if (filtroNombre) {
        // match SOLO por nombre cuando hay texto
        const nameOK = similar(p.nombre || "", filtroNombreRaw);
        return nameOK && catOK;
      }
      // sin texto, respeta categoría si existe
      return catOK;
    });
  }, [products, filtroNombre, filtroNombreRaw, filtroCategoria]);
  
  // 2) Si no hay resultados por nombre, ampliamos a (nombre || categoría ~texto)
  const resultadosAmpliados = useMemo(() => {
    if (!filtroNombre) return [];
    return products.filter((p) => {
      const catExact = filtroCategoria ? normalize(p.categoria || "") === filtroCategoria : true;
      const byName = similar(p.nombre || "", filtroNombreRaw);
      const byCatText = similar(p.categoria || "", filtroNombreRaw);
      return (byName || byCatText) && catExact;
    });
  }, [products, filtroNombre, filtroNombreRaw, filtroCategoria]);
  
  const usandoFallback = filtroNombre && resultadosNombre.length === 0;
  const productosFiltrados = usandoFallback ? resultadosAmpliados : resultadosNombre;

  // Resumen de categorías de los resultados actuales (para mostrar dónde están)
  const resumenCategorias = useMemo(() => {
    const map = new Map(); // key -> { label, count }
    for (const p of productosFiltrados) {
      const key = normalize(p.categoria || "");
      if (!key) continue;
      const prev = map.get(key) || { label: p.categoria, count: 0 };
      prev.count += 1;
      map.set(key, prev);
    }
    // Ordena por mayor cantidad y luego alfabético
    return Array.from(map.entries())
      .map(([key, v]) => ({ key, label: v.label, count: v.count }))
      .sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label));
  }, [productosFiltrados]);
  
  // Sugerencias de categorías (se usan sólo en fallback o pocos resultados)
  const categorias = useMemo(() => {
    const map = new Map();
    for (const p of products) {
      const key = normalize(p.categoria || "");
      if (!key) continue;
      if (!map.has(key)) map.set(key, p.categoria);
    }
    return Array.from(map.entries()).map(([key, label]) => ({ key, label }));
  }, [products]);
  
  const categoriasRelacionadas = useMemo(() => {
    if (!filtroNombre) return [];
    const score = new Map();
    for (const p of products) {
      const catKey = normalize(p.categoria || "");
      if (!catKey) continue;
      const ok =
        similar(p.nombre || "", filtroNombreRaw) ||
        similar(p.categoria || "", filtroNombreRaw);
      if (ok) {
        const prev = score.get(catKey) || { label: p.categoria, count: 0 };
        prev.count += 1;
        score.set(catKey, prev);
      }
    }
    let arr = Array.from(score.entries()).map(([key, v]) => ({ key, label: v.label, count: v.count }));
    if (arr.length === 0) {
      for (const { key, label } of categorias) {
        const d = lev(key, filtroNombre);
        if ((filtroNombre.length >= 5 && d <= 2) || (filtroNombre.length >= 3 && d <= 1) || key.includes(filtroNombre)) {
          arr.push({ key, label, count: 1 });
        }
      }
    }
    arr.sort((a, b) => (b.count - a.count) || a.label.localeCompare(b.label));
    return arr.slice(0, 8);
  }, [products, filtroNombre, filtroNombreRaw, categorias]);

  // Sugerencia de escritura: "¿Quisiste decir...?"
  const sugerenciaNombre = useMemo(() => {
    if (!filtroNombre) return null;
  
    const needle = filtroNombre;
    let bestName = null;
    let bestDist = Infinity;
  
    // recorremos nombres de productos y comparamos por tokens
    for (const p of products) {
      const nombre = p.nombre || "";
      const norm = normalize(nombre);
      if (!norm) continue;
  
      // si ya contiene exactamente la query normalizada, no sugerimos (ya coincidiría)
      if (norm.includes(needle)) continue;
  
      const tokens = norm.split(/[^a-z0-9]+/).filter(Boolean);
      for (const t of tokens) {
        const d = lev(t, needle);
        if (d < bestDist) {
          bestDist = d;
          bestName = nombre; // guardamos con acentos/caso original para mostrar bonito
        }
      }
    }
  
    // Umbral simple por longitud (conservador para evitar sugerencias locas)
    const len = needle.length;
    const threshold = len >= 7 ? 2 : len >= 4 ? 1 : 0;
  
    if (bestName && bestDist <= threshold && normalize(bestName) !== needle) {
      return bestName;
    }
    return null;
  }, [products, filtroNombre]);
  
  const mostrarCTA = !loading && filtroNombre && productosFiltrados.length < 1; // CTA sólo si no hay nada por nombre y estamos en fallback

  const clearFilters = () => {
    const updated = new URLSearchParams(searchParams);
    updated.delete("categoria");
    updated.delete("nombre");
    updated.set("page", "1");
    setSearchParams(updated);
  };

  return (
    <div className="bg-gray-100 min-h-screen p-6">
      <div className="max-w-6xl mx-auto">
        <Breadcrumb categoria={getCategoriaBonita(filtroCategoriaRaw)} nombre={filtroNombre} />

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-2">
          <h2 className="text-2xl font-bold text-[#40767b]">
            {filtroCategoria || filtroNombre
              ? `Filtrado: ${getCategoriaBonita(filtroCategoriaRaw) || ""} ${filtroNombreRaw ? `- “${filtroNombreRaw}”` : ""}`
              : "Catálogo"}
          </h2>

          {(filtroCategoria || filtroNombre) && (
            <button
              onClick={clearFilters}
              className="text-sm px-4 py-2 bg-sky-100 text-sky-700 rounded-md hover:bg-sky-200 transition"
            >
              Quitar filtros
            </button>
          )}
        </div>

        {<div ref={productosRef} className="scroll-mt-24"></div>}

        <CategoriasGrid
          currentCategory={filtroCategoria}
          onCategoryChange={(cat) => {
            const updated = new URLSearchParams(searchParams);
            updated.set("categoria", cat);
            updated.set("page", "1");
            updated.set("scroll", "1"); // <-- si aún no lo tenías, añade esto para forzar el scroll
            setSearchParams(updated);
          }}
        />

        {loading ? (
          <p className="text-center text-gray-500 mt-10">Cargando productos...</p>
        ) : productosFiltrados.length > 0 ? (
          <>
            {mostrarCTA && (
              <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="font-medium text-amber-900">
                      ¿No encuentras lo que buscas? Intenta buscar por categorías
                    </p>
                    {categoriasRelacionadas.length > 0 && (
                      <p className="text-sm text-amber-800 mt-1">
                        Sugerencias según “{filtroNombreRaw}”:
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => {
                      const updated = new URLSearchParams(searchParams);
                      updated.delete("nombre");
                      setSearchParams(updated);
                      if (productosRef.current) {
                        const y = productosRef.current.getBoundingClientRect().top + window.pageYOffset - 80;
                        window.scrollTo({ top: y, behavior: "smooth" });
                      }
                    }}
                    className="self-start sm:self-auto px-4 py-2 rounded-lg bg-amber-600 text-white hover:bg-amber-700"
                  >
                    Ver categorías
                  </button>
                </div>
                {categoriasRelacionadas.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {categoriasRelacionadas.map((c) => (
                      <button
                        key={c.key}
                        onClick={() => {
                          const updated = new URLSearchParams(searchParams);
                          updated.set("categoria", c.label);
                          updated.delete("nombre");
                          updated.set("page", "1");
                          setSearchParams(updated);
                        }}
                        className="px-3 py-1.5 rounded-full bg-white text-amber-900 border border-amber-200 hover:border-amber-300 text-sm"
                      >
                        {c.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
        
            {mostrarCTA && (
              <div className="mb-4 text-sm text-amber-800 bg-amber-100 border border-amber-200 rounded-md px-3 py-2">
                🔍 No encontramos coincidencias exactas por nombre, así que mostramos productos de categorías similares.
              </div>
            )}
        
            {/* 💡 Sugerencia cuando hay fallback pero sí hay resultados */}
            {usandoFallback && sugerenciaNombre && (
              <div className="mb-3 rounded-md border border-sky-200 bg-sky-50 text-sky-900 px-3 py-2 text-sm">
                ¿Quisiste decir{" "}
                <button
                  type="button"
                  onClick={() => {
                    const updated = new URLSearchParams(searchParams);
                    updated.set("nombre", sugerenciaNombre);
                    updated.delete("categoria");
                    updated.set("page", "1");
                    setSearchParams(updated);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="underline font-semibold hover:opacity-80"
                  title="Buscar con esta sugerencia"
                >
                  “{sugerenciaNombre}”
                </button>
                ?
              </div>
            )}
        
            {filtroNombre && resumenCategorias.length > 0 && (
              <div className="mb-4 rounded-lg border border-sky-200 bg-sky-50 p-3">
                <p className="text-sm text-sky-900 mb-2">
                  Resultados encontrados en categorías:
                </p>
                <div className="flex flex-wrap gap-2">
                  {resumenCategorias.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => {
                        const updated = new URLSearchParams(searchParams);
                        updated.set("categoria", c.label);
                        updated.set("page", "1");
                        setSearchParams(updated);
                        window.scrollTo({ top: 0, behavior: "smooth" });
                      }}
                      className="px-3 py-1.5 rounded-full bg-white text-sky-900 border border-sky-200 hover:border-sky-300 text-sm"
                      title={`Ver solo ${c.label}`}
                    >
                      {c.label} <span className="opacity-70">({c.count})</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
        
            <ProductGrid
              products={productosFiltrados}
              paginaActual={paginaActual}
              setSearchParams={setSearchParams}
              productosRef={productosRef}   // <-- pasar el ref aquí
            />
          </>
        ) : (
          <>
            {/* "¿Quisiste decir...?" cuando no hay resultados */}
            {filtroNombre && sugerenciaNombre && (
              <div className="mx-auto max-w-2xl mb-4 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sky-900">
                ¿Quisiste decir{" "}
                <button
                  type="button"
                  onClick={() => {
                    const updated = new URLSearchParams(searchParams);
                    updated.set("nombre", sugerenciaNombre);
                    updated.delete("categoria");
                    updated.set("page", "1");
                    setSearchParams(updated);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  className="underline font-semibold hover:no-underline"
                  title={`Buscar "${sugerenciaNombre}"`}
                >
                  “{sugerenciaNombre}”
                </button>
                ?
              </div>
            )}
        
            <div className="mt-10 text-center">
              <p className="text-gray-600 mb-4">
                No se encontraron productos para “{filtroNombreRaw || filtroCategoriaRaw}”.
              </p>
              <div className="mx-auto max-w-2xl rounded-xl border border-amber-200 bg-amber-50 p-5">
                <p className="font-medium text-amber-900">
                  ¿No encuentras lo que buscas? Intenta buscar por categorías
                </p>
                {categoriasRelacionadas.length > 0 && (
                  <p className="text-sm text-amber-800 mt-1">
                    Sugerencias según “{filtroNombreRaw}”:
                  </p>
                )}
                <div className="mt-3 flex flex-wrap justify-center gap-2">
                  {(categoriasRelacionadas.length > 0 ? categoriasRelacionadas : categorias.slice(0, 10)).map((c) => (
                    <button
                      key={c.key}
                      onClick={() => {
                        const updated = new URLSearchParams(searchParams);
                        updated.set("categoria", c.label);
                        updated.delete("nombre");
                        updated.set("page", "1");
                        setSearchParams(updated);
                      }}
                      className="px-3 py-1.5 rounded-full bg-white text-amber-900 border border-amber-200 hover:border-amber-300 text-sm"
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <button
                  onClick={clearFilters}
                  className="mt-4 inline-flex items-center gap-2 text-sm px-4 py-2 bg-white text-amber-900 rounded-md border border-amber-200 hover:bg-amber-100"
                >
                  Quitar filtros y ver todo
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
